import { useEffect, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

const ICON_SPACING = 8;

type ResizeObserverEntryList = ResizeObserverEntry[];

/**
 *  Receives a ref to a parent wrapper uses a ResizeObserver to keep track of the parent wrapper width
 *  Calculates the width of each child element and returns an array of booleans to say whether there is space for each given element
 */
function useShouldShowChildren<T extends HTMLElement>(): [React.RefObject<T>, boolean[]] {
  const wrapperRef = useRef<T | null>(null);

  const [wrapperWidth, setWrapperWidth] = useState<number>(0);
  const [childrenShowing, setChildrenShowing] = useState<boolean[]>([]);

  // Observe and set the width of the parent wrapper
  useEffect(() => {
    const getWrapperWidth = (entries: ResizeObserverEntryList) => {
      entries.forEach((entry) => {
        setWrapperWidth(Math.floor(entry.contentRect.width));
      });
    };

    const resizeObserver = new ResizeObserver(getWrapperWidth);

    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Calculate whether there is space of for each element (in order) inside the parent wrapper
  useEffect(() => {
    if (!wrapperRef.current) return;

    const childrenArray = Array.from(wrapperRef.current.children);
    const elementWidths = childrenArray.map((element) =>
      element ? Math.floor(element.getBoundingClientRect().width) : 0,
    );

    const updatedShow = elementWidths.map((_, idx) => {
      const renderedGroupWidth = elementWidths
        .slice(0, idx + 1)
        .reduce((sum, width) => sum + width);

      const spacing = idx * ICON_SPACING;

      return renderedGroupWidth <= wrapperWidth - spacing;
    });

    setChildrenShowing(updatedShow);
  }, [wrapperWidth]);

  return [wrapperRef, childrenShowing];
}

export default useShouldShowChildren;
